//* Libraries imports
import { useState, useEffect } from "react";

//* Componentes imports
import { Input } from "./input";

//* Utils imports
import { currencyMask } from "@/helpers/index";

export type NumericInputProps = {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  placeholder?: string;
  id?: string;
  className?: string;
  suffix?: React.ReactNode;
  variant?: "currency" | "percentage" | "integer" | "decimal";
  disabled?: boolean;
  max?: number;
};

export function NumericInput({
  value,
  onChange,
  placeholder,
  id,
  className,
  suffix,
  variant = "decimal",
  disabled,
  max,
}: NumericInputProps) {
  const [displayValue, setDisplayValue] = useState("");

  useEffect(() => {
    if (value === undefined || value === null) {
      setDisplayValue("");
      return;
    }

    if (variant === "currency") {
      const formatted = currencyMask(value.toFixed(2).replace(".", ""));
      setDisplayValue(formatted);
    } else {
      const formatted = value.toString().replace(".", ",");
      if (displayValue.replace(",", ".") !== value.toString()) {
        setDisplayValue(formatted);
      }
    }
  }, [value, variant]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    if (variant === "currency") {
      const digitsOnly = rawValue.replace(/\D/g, "");
      const numericValue = parseFloat(digitsOnly) / 100 || 0;

      setDisplayValue(currencyMask(digitsOnly));
      onChange(numericValue);
      return;
    }

    let processedValue = rawValue.replace(".", ",");

    const regex =
      variant === "integer" || id?.includes("Installments")
        ? /^\d*$/
        : /^\d*,?\d*$/;

    if (processedValue === "" || regex.test(processedValue)) {
      setDisplayValue(processedValue);

      const numericValue =
        variant === "integer" || id?.includes("Installments")
          ? parseInt(processedValue, 10)
          : parseFloat(processedValue.replace(",", "."));

      if (!isNaN(numericValue)) {
        if (max !== undefined && numericValue > max) {
          onChange(max);
          setDisplayValue(max.toString().replace(".", ","));
        } else {
          onChange(numericValue);
        }
      } else if (processedValue === "") {
        onChange(undefined);
      }
    }
  };

  return (
    <div className="relative flex items-center w-full">
      <Input
        id={id}
        type="text"
        placeholder={placeholder}
        value={displayValue}
        onChange={handleInputChange}
        disabled={disabled}
        className={`${className} ${suffix && variant !== "currency" ? "pr-8" : ""}`}
      />
      {suffix && variant !== "currency" && (
        <div className="absolute right-3 text-gray-400 pointer-events-none select-none">
          {suffix}
        </div>
      )}
    </div>
  );
}
